import { Link } from "react-router-dom";
import {
  IdCard, QrCode, Nfc, Share2, BarChart3, Palette, UserPlus, ArrowUpRight,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Bits";
import { useI18n } from "@/i18n/I18nContext";

function Block({ n, icon: Icon, title, desc, tags }) {
  return (
    <div className="group relative glass rounded-3xl p-7 sm:p-8 overflow-hidden card-hover" data-testid={`eco-block-${n}`}>
      <div className="absolute -top-6 -right-4 font-display font-black text-7xl text-white/[0.04] select-none">{n}</div>
      <div className="relative flex items-start gap-4">
        <span className="w-12 h-12 rounded-2xl bg-brand-gradient flex items-center justify-center shrink-0 shadow-lg shadow-cyan-500/10">
          <Icon className="w-6 h-6 text-white" />
        </span>
        <div>
          <p className="font-mono text-xs text-cyan-400">{n}</p>
          <h3 className="mt-0.5 font-display font-bold text-xl text-white">{title}</h3>
        </div>
      </div>
      <p className="relative mt-4 text-zinc-400 leading-relaxed text-sm">{desc}</p>
      <div className="relative mt-5 flex flex-wrap gap-2">
        {tags.map((tg) => (
          <span key={tg.label} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-zinc-300">
            <tg.icon className="w-3.5 h-3.5 text-cyan-400" /> {tg.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Ecosystem() {
  const { t } = useI18n();
  const blocks = [
    { n: "01", icon: IdCard, title: t("home.eco_1_t"), desc: t("home.eco_1_d"),
      tags: [{ icon: IdCard, label: t("home.ecoTag_card") }, { icon: Palette, label: t("home.ecoTag_custom") }] },
    { n: "02", icon: Nfc, title: t("home.eco_2_t"), desc: t("home.eco_2_d"),
      tags: [{ icon: Nfc, label: t("home.ecoTag_nfc") }, { icon: Palette, label: t("home.ecoTag_custom") }] },
    { n: "03", icon: Share2, title: t("home.eco_3_t"), desc: t("home.eco_3_d"),
      tags: [{ icon: QrCode, label: t("home.ecoTag_qr") }, { icon: Share2, label: t("home.ecoTag_share") }, { icon: UserPlus, label: t("home.ecoTag_add") }] },
    { n: "04", icon: BarChart3, title: t("home.eco_4_t"), desc: t("home.eco_4_d"),
      tags: [{ icon: BarChart3, label: t("home.ecoTag_stats") }] },
  ];

  return (
    <section className="relative overflow-hidden py-24" data-testid="home-ecosystem">
      <div className="aura w-[520px] h-[520px] bg-violet-700/15 -top-20 right-0" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <Eyebrow>{t("home.ecoEyebrow")}</Eyebrow>
            <h2 className="mt-4 font-display font-black tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              <span className="text-white">{t("home.ecoTitle1")}</span>{" "}
              <span className="text-gradient">{t("home.ecoTitle2")}</span>
            </h2>
            <p className="mt-6 text-lg text-zinc-400 leading-relaxed">{t("home.ecoDesc")}</p>
          </div>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {blocks.map((b, i) => (
            <Reveal key={b.n} delay={i * 0.08}>
              <Block {...b} />
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-14 flex justify-center">
            <Link to="/nfc" data-testid="eco-cta"
              className="group inline-flex items-center gap-2 bg-brand-gradient text-white font-semibold px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity duration-300">
              {t("home.ecoCta")}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
